import type { IconDefinition } from '@fortawesome/pro-regular-svg-icons';
import type { ButtonProps, DialogProps } from '@mui/material';

import * as React from 'react';
import { Dialog, FontAwesomeScalableIcon } from '@mend/mui';
import { Avatar, Box, Button, Card, Stack, Typography } from '@mui/material';

const Context = React.createContext<string | null>(null);

function useOutreachDialogTitleId() {
  const titleId = React.useContext(Context);

  if (!titleId) {
    throw new Error('OutreachDialog components must be rendered within OutreachDialog.Root');
  }

  return titleId;
}

export type Status = 'idle' | 'loading' | 'submitting' | 'error';

export interface Recipient {
  id: number;
  name: string;
  appointmentLabel: string;
  providerName?: string;
}

export function Root(props: {
  children: React.ReactNode;
  maxWidth?: DialogProps['maxWidth'];
  onClose: () => void;
}) {
  const titleId = React.useId();

  return (
    <Context.Provider value={titleId}>
      <Dialog.Root
        open
        onClose={props.onClose}
        maxWidth={props.maxWidth ?? 'sm'}
        fullWidth
        aria-labelledby={titleId}
      >
        {props.children}
      </Dialog.Root>
    </Context.Provider>
  );
}

export function Header(props: {
  children: React.ReactNode;
  color: Exclude<NonNullable<ButtonProps['color']>, 'inherit'>;
  icon: IconDefinition;
}) {
  const titleId = useOutreachDialogTitleId();

  return (
    <Dialog.Header>
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <Avatar sx={{ bgcolor: `${props.color}.main`, color: `${props.color}.contrastText` }}>
          <FontAwesomeScalableIcon icon={props.icon} fontSize="small" />
        </Avatar>

        <Box flex={1}>
          <Dialog.Title id={titleId}>{props.children}</Dialog.Title>
        </Box>
      </Stack>
    </Dialog.Header>
  );
}

export function Content(props: { children: React.ReactNode }) {
  return <Dialog.Content>{props.children}</Dialog.Content>;
}

export function RecipientDetails(props: { recipient: Recipient }) {
  return (
    <Stack direction="row" spacing={2} mb={2}>
      <InfoItem label="Patient" value={props.recipient.name} />

      {props.recipient.providerName ? (
        <InfoItem label="Provider" value={props.recipient.providerName} />
      ) : null}

      <InfoItem label="Appointment" value={props.recipient.appointmentLabel} />
    </Stack>
  );
}

export function MessagePreview(props: { children: React.ReactNode; label: React.ReactNode }) {
  return (
    <Box>
      <Typography component="p" variant="caption" color="text.secondary" textTransform="uppercase">
        {props.label}
      </Typography>

      <Card variant="outlined" sx={{ padding: 1.5, marginTop: 0.5, bgcolor: 'background.default' }}>
        <Typography component="div" variant="body2" whiteSpace="pre-line">
          {props.children}
        </Typography>
      </Card>
    </Box>
  );
}

export function RecipientTableSection(props: {
  children: React.ReactNode;
  label?: React.ReactNode;
  note?: React.ReactNode;
}) {
  return (
    <>
      {props.note ? (
        <Typography component="p" variant="caption" color="text.secondary" marginTop={1}>
          {props.note}
        </Typography>
      ) : null}

      <Typography
        component="p"
        variant="caption"
        color="text.secondary"
        textTransform="uppercase"
        marginTop={2}
        marginBottom={0.5}
      >
        {props.label ?? 'Recipients'}
      </Typography>

      {props.children}
    </>
  );
}

/**
 * The caveat every bulk preview carries: the copy shown is illustrative, because the message each
 * recipient receives is written server-side at send time. Each flow words its own sentence — how
 * the copy is generated differs — but the treatment is shared.
 */
export function GenerationNote(props: { children: React.ReactNode }) {
  return (
    <Typography component="p" variant="caption" color="text.secondary" marginTop={1}>
      {props.children}
    </Typography>
  );
}

export function DeliverySummary(props: { children: React.ReactNode }) {
  return (
    <Typography component="p" variant="caption" color="text.secondary" marginTop={2}>
      {props.children}
    </Typography>
  );
}

export function Footer(props: {
  color: Exclude<NonNullable<ButtonProps['color']>, 'inherit'>;
  confirmLabel: string;
  icon: IconDefinition;
  onConfirm: () => void;
  status: Status;
}) {
  return (
    <Dialog.Footer>
      <Dialog.DismissButton>Cancel</Dialog.DismissButton>

      <Button
        variant="contained"
        color={props.color}
        size="large"
        startIcon={<FontAwesomeScalableIcon icon={props.icon} />}
        loading={props.status === 'submitting'}
        loadingPosition="start"
        disabled={props.status !== 'idle'}
        onClick={props.onConfirm}
      >
        {props.confirmLabel}
      </Button>
    </Dialog.Footer>
  );
}

export function InfoItem(props: { label: string; value: string }) {
  return (
    <Card variant="outlined" sx={{ flex: 1, padding: 1.5, bgcolor: 'background.default' }}>
      <Typography component="p" variant="caption" color="text.secondary" textTransform="uppercase">
        {props.label}
      </Typography>
      <Typography component="p" variant="body2">
        {props.value}
      </Typography>
    </Card>
  );
}
